/**
 * Shared constants and helpers of the chart view (wheel + tables + glossary popup).
 * Moved unchanged from screens/me/NatalChart.tsx.
 */

import type { NatalSelectKind } from './NatalChartSvg';
import {
  SIGN_GLOSSARY, PLANET_GLOSSARY, HOUSE_GLOSSARY, ANGLE_GLOSSARY,
  type GlossaryEntry,
} from './natalGlossary';

/** Minimal shape of the i18next `t` we pass into module helpers. */
export type TF = (key: string, options?: Record<string, unknown>) => string;

export const PLANET_GLYPH: Record<string, string> = {
  sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
  jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇',
  north_node: '☊', south_node: '☋', lilith: '⚸', chiron: '⚷',
};

export const SIGN_GLYPH: Record<string, string> = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋',
  Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏',
  Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
};

export const PLANET_NAME: Record<string, string> = {
  sun: 'Sun', moon: 'Moon', mercury: 'Mercury', venus: 'Venus', mars: 'Mars',
  jupiter: 'Jupiter', saturn: 'Saturn', uranus: 'Uranus', neptune: 'Neptune', pluto: 'Pluto',
  north_node: 'North Node', south_node: 'South Node', lilith: 'Lilith', chiron: 'Chiron',
};

export const ASPECT_GLYPH: Record<string, string> = {
  conjunction: '☌', opposition: '☍', trine: '△', square: '□',
  sextile: '✶', quincunx: '⚻', semisextile: '⚺',
};

export const ANGLE_LABEL: Record<string, string> = { asc: 'Asc', mc: 'MC', ic: 'IC', dc: 'DC' };

export interface PopupState {
  entry: GlossaryEntry;
  glyph?: string;
}

export function fmtDeg(n: number): string {
  return `${n.toFixed(1)}°`;
}

export function planetLabel(id: string, t: TF): string {
  return t(`natalChart.names.planets.${id}`, { defaultValue: PLANET_NAME[id] ?? id.replace(/_/g, ' ') });
}

export function signLabel(sign: string, t: TF): string {
  return t(`natalChart.names.signs.${sign}`, { defaultValue: sign });
}

export function signShort(sign: string, t: TF): string {
  return t(`natalChart.names.signsShort.${sign}`, { defaultValue: sign.slice(0, 3) });
}

/** Resolve a glossary entry's display strings through i18n, falling back to the
 *  English entry from natalGlossary.ts. */
export function localizeEntry(category: string, id: string, entry: GlossaryEntry, t: TF): GlossaryEntry {
  return {
    title: t(`natalChart.glossary.${category}.${id}.title`, { defaultValue: entry.title }),
    subtitle: entry.subtitle
      ? t(`natalChart.glossary.${category}.${id}.subtitle`, { defaultValue: entry.subtitle })
      : undefined,
    body: t(`natalChart.glossary.${category}.${id}.body`, { defaultValue: entry.body }),
  };
}

export function resolveSelection(kind: NatalSelectKind, id: string, t: TF): PopupState | null {
  switch (kind) {
    case 'sign': {
      const entry = SIGN_GLOSSARY[id];
      return entry ? { entry: localizeEntry('sign', id, entry, t), glyph: SIGN_GLYPH[id] } : null;
    }
    case 'planet': {
      const entry = PLANET_GLOSSARY[id];
      return entry ? { entry: localizeEntry('planet', id, entry, t), glyph: PLANET_GLYPH[id] } : null;
    }
    case 'house': {
      const n = Number(id);
      const entry = HOUSE_GLOSSARY[n];
      return entry ? { entry: localizeEntry('house', id, entry, t), glyph: String(n) } : null;
    }
    case 'angle': {
      const entry = ANGLE_GLOSSARY[id];
      return entry ? { entry: localizeEntry('angle', id, entry, t) } : null;
    }
    default:
      return null;
  }
}
